// Assignment approval and completion states for typed and historical cards.
import { type ReactNode } from 'react';

import { type PermissionStatus } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call';
import { AssistantCardFrame } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call_question_views';
import {
    type ApprovalEditor,
    type AssignmentResult,
    type PermissionOutcome,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import {
    AssignFillCard,
    type AssignFillStatus,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/assign_fill_card';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';

export function AssignApprovalView({
    editor,
    status,
    outcome,
    assignmentResult,
    submitting,
    onAllow,
    onDeny,
    statusText,
}: Readonly<{
    readonly editor: Extract<ApprovalEditor, { readonly kind: 'assign_fill' }>;
    readonly status: PermissionStatus;
    readonly outcome: PermissionOutcome;
    readonly assignmentResult?: AssignmentResult;
    readonly statusText?: string;
    readonly submitting: boolean;
    readonly onAllow: (argumentsOverride: Record<string, unknown>) => void;
    readonly onDeny: () => void;
}>): ReactNode {
    const { formatName } = useFormatPersonName();
    const shifts = editor.shifts;
    const cardStatus = assignmentCardStatus(
        status,
        outcome,
        assignmentResult,
        shifts.map((shift) => shift.id),
    );
    const assignedIds = new Set(
        assignmentResult?.shifts
            .filter(
                (shift) =>
                    shift.assigned &&
                    shifts.some((selected) => selected.id === shift.id),
            )
            .map((shift) => shift.id),
    );
    return (
        <AssistantCardFrame statusText={statusText}>
            <AssignFillCard
                caregiverName={formatName(editor.caregiver_name)}
                shifts={shifts.map((shift) => ({
                    id: shift.id,
                    name: formatName(shift.name),
                    detail: shift.detail,
                    ...(cardStatus === 'partial'
                        ? {
                              resultLabel: assignedIds.has(shift.id)
                                  ? 'Assigned'
                                  : 'Not assigned',
                          }
                        : {}),
                    ...(shift.recurrence_label === null
                        ? {}
                        : { recurrenceLabel: shift.recurrence_label }),
                }))}
                status={cardStatus}
                notices={(editor.notices ?? [])
                    .filter(
                        (notice) =>
                            notice.shift_ids === null ||
                            notice.shift_ids.some((id) =>
                                shifts.some((shift) => shift.id === id),
                            ),
                    )
                    .map((notice) => notice.text)}
                statusLabel={
                    status === 'expired'
                        ? 'Request expired'
                        : cardStatus === 'partial'
                          ? `${assignedIds.size} of ${shifts.length} shifts assigned`
                          : undefined
                }
                onAssign={() =>
                    onAllow({ shift_ids: shifts.map((shift) => shift.id) })
                }
                onDeny={onDeny}
                disabled={submitting || shifts.length === 0}
            />
        </AssistantCardFrame>
    );
}

function assignmentCardStatus(
    status: PermissionStatus,
    outcome: PermissionOutcome,
    result?: AssignmentResult,
    selectedIds?: ReadonlyArray<string>,
): AssignFillStatus {
    if (status === 'waiting') {
        return 'pending';
    }
    if (status !== 'granted') {
        return 'denied';
    }
    if (outcome === 'pending') {
        return 'assigning';
    }
    if (outcome === 'failed') {
        return 'failed';
    }
    // Historical results did not carry assignment details. New assignment
    // outputs are validated before entering the registry.
    if (result === undefined) {
        return 'assigned';
    }
    if (
        result.status === 'assigned' &&
        result.shifts.length > 0 &&
        result.shifts.every((shift) => shift.assigned) &&
        (result.notices?.length ?? 0) === 0 &&
        (selectedIds === undefined ||
            selectedIds.every((id) =>
                result.shifts.some(
                    (shift) => shift.id === id && shift.assigned,
                ),
            ))
    ) {
        return 'assigned';
    }
    return result.shifts.some(
        (shift) =>
            shift.assigned &&
            (selectedIds === undefined || selectedIds.includes(shift.id)),
    )
        ? 'partial'
        : 'failed';
}

export function LegacyAssignmentApprovalView({
    caregiverName,
    shifts,
    status,
    outcome,
    assignmentResult,
    submitting,
    statusText,
    onAllow,
    onDeny,
}: Readonly<{
    caregiverName: string;
    shifts: ReadonlyArray<{ id: string; name: string; detail: string }>;
    status: PermissionStatus;
    outcome: PermissionOutcome;
    assignmentResult?: AssignmentResult;
    submitting: boolean;
    statusText?: string;
    onAllow: () => void;
    onDeny: () => void;
}>): ReactNode {
    return (
        <AssistantCardFrame statusText={statusText}>
            <AssignFillCard
                caregiverName={caregiverName}
                shifts={shifts}
                status={assignmentCardStatus(status, outcome, assignmentResult)}
                onAssign={onAllow}
                onDeny={onDeny}
                disabled={submitting}
            />
        </AssistantCardFrame>
    );
}
